import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { api, formatApiError } from "../../api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { toast } from "../ui/sonner";
import { formatCurrency } from "../../lib/format";
import { MONETIZATION } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";

function currentPeriod() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

// UX-CP-02 §2 SCR-P08-33 "Ad-share settlement review" — `POST /broadcast/settlement/{period}`
// (Prompt 11) is already idempotent (re-running a settled period reports "already_settled",
// never double-posts), so a single "Run" action doubles as the safe preview+confirm step —
// there's no separate preview-only read endpoint to call first.
export function AdShareSettlementPanel() {
  const [period, setPeriod] = useState(currentPeriod());
  const reducedMotion = useReducedMotion();

  const runMutation = useMutation({
    mutationFn: () => api.post(`/broadcast/settlement/${period}`, { organization_ids: null }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <motion.div {...fadeUp(reducedMotion, 0.04)} className="solid-surface rounded-[14px] p-5 space-y-4">
      <p className="eyebrow">Ad-share settlement</p>
      <div className="flex items-end gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="settlement-period">Period (YYYY-MM)</Label>
          <Input id="settlement-period" data-testid={MONETIZATION.settlementPeriodInput} value={period} onChange={(e) => setPeriod(e.target.value)} className="max-w-[140px]" />
        </div>
        <Button
          data-testid={MONETIZATION.settlementRunButton}
          disabled={runMutation.isPending}
          onClick={() => runMutation.mutate()}
          className="text-white"
          style={{ background: "var(--scarlet-accent)" }}
        >
          Run settlement for every gym
        </Button>
      </div>

      {runMutation.data && (
        <Table data-testid={MONETIZATION.settlementResultsTable}>
          <TableHeader>
            <TableRow><TableHead>Gym</TableHead><TableHead>Status</TableHead><TableHead>Amount</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {runMutation.data.data.results.map((r) => (
              <TableRow key={r.organization_id}>
                <TableCell className="font-mono text-xs">{r.organization_id}</TableCell>
                <TableCell className="capitalize">{r.status.replace("_", " ")}</TableCell>
                <TableCell>{formatCurrency(r.amount)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </motion.div>
  );
}
